import { Button } from "@/components/ui/button";
import { cn } from "./cn.js";
import { SiteGenerativePane } from "./SiteGenerativePane.js";
import { SiteSourcePane } from "./SiteSourcePane.js";
import {
	parseWebsiteLive,
	websitePreviewReady,
	type WebsiteLive,
} from "./site-generative.js";
import {
	ArrowLeftIcon,
	ArrowRightIcon,
	CodeIcon,
	ExternalLinkIcon,
	EyeIcon,
	GlobeIcon,
	LoaderCircleIcon,
	LockIcon,
	MonitorIcon,
	RefreshCwIcon,
	RocketIcon,
	SmartphoneIcon,
	TabletIcon,
	TerminalIcon,
	XIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

type Viewport = "desktop" | "tablet" | "mobile";
type CanvasMode = "preview" | "code";

type Props = {
	url: string;
	title?: string;
	onClose: () => void;
	/** Routes through Mairko's gated publish_site — same path as the Sites tile. */
	onPublish?: (siteId: string, siteName: string) => void;
};

const VIEWPORT_KEY = "mairko.sitePreview.viewport";
const MODE_KEY = "mairko.sitePreview.mode";

const VIEWPORTS: Record<
	Viewport,
	{ label: string; maxWidth: string; icon: typeof MonitorIcon }
> = {
	desktop: { label: "Desktop", maxWidth: "1280px", icon: MonitorIcon },
	tablet: { label: "Tablet", maxWidth: "768px", icon: TabletIcon },
	mobile: { label: "Mobile", maxWidth: "390px", icon: SmartphoneIcon },
};

const PREVIEW_SITE_RE =
	/\/api\/sites\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/preview(?:\/(.*))?$/i;

function hostedPreviewParts(
	url: string,
): { siteId: string; hint: string } | null {
	try {
		const path = url.startsWith("http")
			? new URL(url).pathname
			: (url.split("?")[0] ?? url).split("#")[0] ?? url;
		const match = path.match(PREVIEW_SITE_RE);
		if (!match?.[1]) return null;
		return { siteId: match[1], hint: match[2] ?? "index.html" };
	} catch {
		return null;
	}
}

function readViewport(): Viewport {
	try {
		const raw = sessionStorage.getItem(VIEWPORT_KEY);
		if (raw === "desktop" || raw === "tablet" || raw === "mobile") return raw;
	} catch {
		/* ignore */
	}
	return "desktop";
}

function readMode(): CanvasMode {
	try {
		const raw = sessionStorage.getItem(MODE_KEY);
		if (raw === "preview" || raw === "code") {
			return raw;
		}
	} catch {
		/* ignore */
	}
	return "preview";
}

function urlRequestsCode(url: string): boolean {
	try {
		const parsed = url.startsWith("http")
			? new URL(url)
			: new URL(url, "https://matrix.local");
		return parsed.hash === "#code" || parsed.searchParams.get("view") === "code";
	} catch {
		return url.includes("#code") || url.includes("view=code");
	}
}

function modeFromUrl(url: string): CanvasMode {
	return urlRequestsCode(url) ? "code" : readMode();
}

/**
 * Dominant side canvas: live iframe, plus a Code toggle that reads the
 * Astro source we already stored in R2 (no GitHub, no publish).
 */
export function SitePreview({ url, title, onClose, onPublish }: Props) {
	const hosted = hostedPreviewParts(url);
	const label = title?.trim() || hostLabel(url);
	const showUrl = !hosted && !url.startsWith("/api/sites/");
	const [viewport, setViewport] = useState<Viewport>(() => readViewport());
	const [mode, setMode] = useState<CanvasMode>(() =>
		hosted ? modeFromUrl(url) : "preview",
	);
	const [frameKey, setFrameKey] = useState(0);
	const [terminalOpen, setTerminalOpen] = useState(true);
	const [publishClicked, setPublishClicked] = useState(false);
	// Browser chrome: the address shows where the iframe actually is (updates
	// as the user clicks links inside the site); typing a path navigates.
	const frameRef = useRef<HTMLIFrameElement | null>(null);
	const [address, setAddress] = useState("/");
	const [navTarget, setNavTarget] = useState<string | null>(null);
	const live = useHostedWebsiteLive(hosted?.siteId ?? null);
	const frame = VIEWPORTS[viewport];
	const websiteSrc =
		hosted && navTarget
			? `/api/sites/${hosted.siteId}/preview${navTarget}`
			: previewSrc(url);
	const showLiveFrame = !hosted || websitePreviewReady(live);
	const studio = hosted ? (live?.studio ?? null) : null;
	const generating =
		live?.phase === "generating" ||
		live?.phase === "building" ||
		live?.status === "building";
	const rebuild = hosted ? (live?.rebuild ?? null) : null;
	const rebuilding = rebuild?.state === "running";
	const publishing = publishClicked || live?.status === "publishing";
	const publishedUrl = live?.publishedUrl ?? null;
	const canPublish =
		Boolean(hosted && onPublish) &&
		live?.status === "preview_ready" &&
		!publishedUrl;

	// When a rebuild finishes, reload the iframe so the edit shows up.
	const wasRebuilding = useRef(false);
	useEffect(() => {
		if (wasRebuilding.current && !rebuilding) {
			setFrameKey((n) => n + 1);
		}
		wasRebuilding.current = rebuilding;
	}, [rebuilding]);

	// Vite HMR often cannot reach the iframe through the Worker preview
	// proxy. Reload when the runner reports a write so the live tree shows.
	const stepSig = studio?.steps.join("\n") ?? "";
	const lastStepSig = useRef("");
	useEffect(() => {
		if (!studio?.live || !stepSig || stepSig === lastStepSig.current) {
			lastStepSig.current = stepSig;
			return;
		}
		lastStepSig.current = stepSig;
		setFrameKey((n) => n + 1);
	}, [stepSig, studio?.live]);

	useEffect(() => {
		if (live?.status === "published" || live?.status === "publishing") {
			setPublishClicked(false);
		}
	}, [live?.status]);

	// If the approval is rejected (status never leaves preview_ready), give the
	// button back instead of spinning forever.
	useEffect(() => {
		if (!publishClicked) return;
		const timer = window.setTimeout(() => setPublishClicked(false), 20_000);
		return () => window.clearTimeout(timer);
	}, [publishClicked]);

	// Publish stays gated: the button asks Mairko, which proposes the
	// publish_site action and renders the approval card in chat.
	const startPublish = () => {
		if (!hosted || !onPublish || publishing) return;
		setPublishClicked(true);
		onPublish(hosted.siteId, live?.name?.trim() || label);
	};

	/** Site-relative path of whatever the iframe currently shows ("/about"). */
	const currentFramePath = (): string | null => {
		if (!hosted) return null;
		try {
			const raw = frameRef.current?.contentWindow?.location.pathname;
			if (!raw) return null;
			const inner = raw
				.replace(new RegExp(`^/api/sites/${hosted.siteId}/preview`, "i"), "")
				.replace(/\/index\.html$/i, "/");
			return inner.startsWith("/") ? inner : `/${inner}`;
		} catch {
			return null;
		}
	};

	const handleFrameLoad = () => {
		const path = currentFramePath();
		if (path) setAddress(path);
	};

	const navigateTo = (raw: string) => {
		if (!hosted) return;
		let path = raw.trim();
		if (!path) return;
		// People paste full URLs or type "about" — normalize to a site path.
		try {
			if (/^https?:\/\//i.test(path)) path = new URL(path).pathname;
		} catch {
			/* keep as typed */
		}
		if (!path.startsWith("/")) path = `/${path}`;
		setAddress(path);
		setNavTarget(path);
		setFrameKey((n) => n + 1);
	};

	const reloadFrame = () => {
		// Reload where the user actually is, not where the canvas started.
		const path = currentFramePath();
		if (path) setNavTarget(path);
		setFrameKey((n) => n + 1);
	};

	useEffect(() => {
		try {
			sessionStorage.setItem(VIEWPORT_KEY, viewport);
		} catch {
			/* ignore */
		}
	}, [viewport]);

	useEffect(() => {
		if (!hosted) {
			setMode("preview");
			return;
		}
		// Only honor an explicit Code deep-link. A later url tweak must not
		// yank them off Website — that remount was blasting the pane and
		// cancelling the Code fetch.
		if (urlRequestsCode(url)) setMode("code");
	}, [url, hosted]);

	useEffect(() => {
		if (!hosted) return;
		try {
			sessionStorage.setItem(MODE_KEY, mode);
		} catch {
			/* ignore */
		}
	}, [hosted, mode]);

	return (
		<aside className="mc-site-preview" aria-label="Site preview">
			<header className="mc-site-preview-bar">
				{hosted ? (
					<div
						className="mc-site-preview-modes"
						role="group"
						aria-label="Website"
					>
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className={cn(
								"mc-site-preview-mode h-7 gap-1 px-2 text-[11px]",
								mode === "preview" && "mc-site-preview-mode--active",
							)}
							aria-pressed={mode === "preview"}
							onClick={() => setMode("preview")}
						>
							<EyeIcon className="size-3.5" />
							Website
						</Button>
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className={cn(
								"mc-site-preview-mode h-7 gap-1 px-2 text-[11px]",
								mode === "code" && "mc-site-preview-mode--active",
							)}
							aria-pressed={mode === "code"}
							onClick={() => setMode("code")}
						>
							<CodeIcon className="size-3.5" />
							Code
						</Button>
					</div>
				) : (
					<div className="mc-site-preview-meta">
						<p className="mc-site-preview-title">{label}</p>
						{showUrl ? <p className="mc-site-preview-url">{url}</p> : null}
					</div>
				)}
				<div className="mc-site-preview-grow" aria-hidden />
				{mode === "preview" ? (
					<div
						className="mc-site-preview-viewports"
						role="group"
						aria-label="Preview width"
					>
						{(Object.keys(VIEWPORTS) as Viewport[]).map((id) => {
							const row = VIEWPORTS[id];
							const Icon = row.icon;
							return (
								<Button
									key={id}
									type="button"
									variant="ghost"
									size="sm"
									className={cn(
										"mc-site-preview-vp h-7 gap-1 px-2 text-[11px]",
										viewport === id && "mc-site-preview-vp--active",
									)}
									aria-pressed={viewport === id}
									title={row.label}
									onClick={() => setViewport(id)}
								>
									<Icon className="size-3.5" />
									<span className="hidden sm:inline">{row.label}</span>
								</Button>
							);
						})}
					</div>
				) : null}
				<div className="mc-site-preview-actions">
					{rebuilding ? (
						<span className="mc-site-preview-rebuild" role="status">
							<LoaderCircleIcon className="size-3.5 animate-spin" />
							Rebuilding…
						</span>
					) : rebuild?.state === "failed" ? (
						<span
							className="mc-site-preview-rebuild mc-site-preview-rebuild--failed"
							role="status"
							title={rebuild.message ?? undefined}
						>
							Build failed
						</span>
					) : null}
					{hosted ? (
						<Button
							type="button"
							variant="ghost"
							size="icon"
							className={cn(
								"size-8",
								terminalOpen && "mc-site-preview-mode--active",
							)}
							title="Build output"
							aria-pressed={terminalOpen}
							onClick={() => setTerminalOpen((open) => !open)}
						>
							<TerminalIcon className="size-3.5" />
						</Button>
					) : null}
					{mode === "preview" ? (
						<Button
							type="button"
							variant="ghost"
							size="icon"
							className="size-8"
							title="Reload"
							onClick={reloadFrame}
						>
							<RefreshCwIcon className="size-3.5" />
						</Button>
					) : null}
					<a
						href={url}
						target="_blank"
						rel="noreferrer"
						className="inline-flex size-8 items-center justify-center rounded-md text-mx-tertiary hover:bg-mx-bg hover:text-mx-label"
						title="Open in new tab"
					>
						<ExternalLinkIcon className="size-3.5" />
					</a>
					{publishedUrl ? (
						<a
							href={publishedUrl}
							target="_blank"
							rel="noreferrer"
							className="mc-site-preview-publish mc-site-preview-publish--live"
							title="Open the live site"
						>
							<GlobeIcon className="size-3.5" />
							Live
						</a>
					) : publishing ? (
						<span className="mc-site-preview-publish" role="status">
							<LoaderCircleIcon className="size-3.5 animate-spin" />
							Publishing…
						</span>
					) : canPublish ? (
						<Button
							type="button"
							size="sm"
							className="mc-site-preview-publish-btn h-7 gap-1.5 px-3 text-[11px]"
							onClick={startPublish}
						>
							<RocketIcon className="size-3.5" />
							Publish
						</Button>
					) : null}
					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="size-8"
						title="Close preview"
						onClick={onClose}
					>
						<XIcon className="size-3.5" />
					</Button>
				</div>
			</header>
			{hosted && mode === "preview" ? (
				<div className="mc-site-preview-addressbar" role="toolbar" aria-label="Browser controls">
					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="size-7 shrink-0"
						title="Back"
						onClick={() => {
							try {
								frameRef.current?.contentWindow?.history.back();
							} catch {
								/* ignore */
							}
						}}
					>
						<ArrowLeftIcon className="size-3.5" />
					</Button>
					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="size-7 shrink-0"
						title="Forward"
						onClick={() => {
							try {
								frameRef.current?.contentWindow?.history.forward();
							} catch {
								/* ignore */
							}
						}}
					>
						<ArrowRightIcon className="size-3.5" />
					</Button>
					<form
						className="mc-site-preview-address"
						onSubmit={(event) => {
							event.preventDefault();
							navigateTo(address);
						}}
					>
						<LockIcon className="size-3 shrink-0 opacity-60" aria-hidden />
						<span className="mc-site-preview-address-host">{label}</span>
						<input
							value={address}
							onChange={(event) => setAddress(event.target.value)}
							onFocus={(event) => event.target.select()}
							spellCheck={false}
							autoComplete="off"
							aria-label="Page path"
						/>
					</form>
				</div>
			) : null}
			<div className="mc-site-preview-body">
				{hosted ? (
					<div
						className={cn(
							"mc-site-preview-layer",
							mode !== "code" && "mc-site-preview-layer--idle",
						)}
						aria-hidden={mode !== "code"}
						inert={mode !== "code" ? true : undefined}
					>
						<SiteSourcePane
							siteId={hosted.siteId}
							hint={hosted.hint}
							previewUrl={websiteSrc}
						/>
					</div>
				) : null}
				<div
					className={cn(
						"mc-site-preview-layer",
						mode !== "preview" && "mc-site-preview-layer--idle",
					)}
					aria-hidden={mode !== "preview"}
					inert={mode !== "preview" ? true : undefined}
				>
					<div className="mc-site-preview-stage">
						<div
							className={cn(
								"mc-site-preview-device",
								`mc-site-preview-device--${viewport}`,
							)}
							style={{ maxWidth: frame.maxWidth }}
						>
							{showLiveFrame ? (
								<iframe
									key={frameKey}
									ref={frameRef}
									id="mc-site-preview-frame"
									className="mc-site-preview-frame"
									title={label}
									src={websiteSrc}
									onLoad={handleFrameLoad}
									sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
									referrerPolicy="no-referrer-when-downgrade"
								/>
							) : (
								<SiteGenerativePane live={live} title={label} />
							)}
						</div>
					</div>
				</div>
			</div>
			{hosted && terminalOpen ? (
				<BuildTerminal
					rebuild={rebuild}
					studio={studio}
					generating={Boolean(generating)}
				/>
			) : null}
			{hosted ? null : (
				<p className="mc-site-preview-note">
					Some websites block embedding. Open in a new tab if this
					stays blank.
				</p>
			)}
		</aside>
	);
}

/** npm / astro / agent output — generate uses studio.log; edits use rebuild.log. */
function BuildTerminal({
	rebuild,
	studio,
	generating,
}: {
	rebuild: WebsiteLive["rebuild"];
	studio: WebsiteLive["studio"];
	generating: boolean;
}) {
	const scroller = useRef<HTMLDivElement | null>(null);
	const lines = studio?.log.length ? studio.log : (rebuild?.log ?? []);
	const lineCount = lines.length;

	useEffect(() => {
		const node = scroller.current;
		if (node) node.scrollTop = node.scrollHeight;
	}, [lineCount]);

	const running = generating || studio?.live || rebuild?.state === "running";

	return (
		<div className="mc-site-terminal" role="log" aria-label="Build output">
			<div className="mc-site-terminal-head">
				<TerminalIcon className="size-3" />
				<span>{generating || studio?.live ? "Terminal" : "Build output"}</span>
				{running ? (
					<span className="mc-site-terminal-state">running</span>
				) : rebuild?.state === "failed" ? (
					<span className="mc-site-terminal-state mc-site-terminal-state--failed">
						failed
					</span>
				) : rebuild?.state === "done" || studio ? (
					<span className="mc-site-terminal-state mc-site-terminal-state--done">
						done
					</span>
				) : null}
			</div>
			{studio?.steps.length ? (
				<div className="mc-site-terminal-steps">
					{studio.steps.slice(-6).map((step) => (
						<p key={step} className="mc-site-terminal-step">
							{step}
						</p>
					))}
				</div>
			) : null}
			<div className="mc-site-terminal-body" ref={scroller}>
				{lineCount === 0 ? (
					<p className="mc-site-terminal-empty">
						{generating
							? "Starting the project… npm install and astro land here."
							: "No builds yet this session. Ask Mairko for a change — the build streams here."}
					</p>
				) : (
					lines.map((line, index) => (
						// eslint-disable-next-line react/no-array-index-key
						<p key={index} className="mc-site-terminal-line">
							{line}
						</p>
					))
				)}
				{rebuild?.state === "failed" && rebuild.message ? (
					<p className="mc-site-terminal-line mc-site-terminal-line--error">
						{rebuild.message}
					</p>
				) : null}
			</div>
		</div>
	);
}

function previewSrc(url: string): string {
	try {
		const parsed = url.startsWith("http")
			? new URL(url)
			: new URL(url, "https://matrix.local");
		parsed.hash = "";
		parsed.searchParams.delete("view");
		if (url.startsWith("http")) return parsed.toString();
		return `${parsed.pathname}${parsed.search}`;
	} catch {
		return url.split("#")[0] ?? url;
	}
}

function useHostedWebsiteLive(siteId: string | null): WebsiteLive | null {
	const [live, setLive] = useState<WebsiteLive | null>(null);

	useEffect(() => {
		if (!siteId) {
			setLive(null);
			return;
		}
		let cancelled = false;
		const tick = async () => {
			try {
				const response = await fetch(`/api/sites/${siteId}/status`, {
					credentials: "include",
				});
				if (!response.ok || cancelled) return;
				const json: unknown = await response.json();
				if (!json || typeof json !== "object") return;
				const rec = json as { ok?: unknown; site?: Record<string, unknown> };
				if (rec.ok !== true || !rec.site || cancelled) return;
				setLive(parseWebsiteLive(rec.site));
			} catch {
				/* ignore */
			}
		};
		void tick();
		const timer = window.setInterval(() => void tick(), 1500);
		return () => {
			cancelled = true;
			window.clearInterval(timer);
		};
	}, [siteId]);

	return live;
}

function hostLabel(url: string): string {
	try {
		return new URL(url).hostname.replace(/^www\./, "");
	} catch {
		const hosted = hostedPreviewParts(url);
		return hosted ? "Working copy" : "Site preview";
	}
}
